import client from './client';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { sendLog, LogLevel } from '@/api/client-logs';
import store from '@/store';
import ApiCancelError from '@/resources/error/ApiCancelError';
import { ErrorCode } from '@/resources/error/ErrorCode';

const template = async <T>(request: () => Promise<AxiosResponse<T>>): Promise<AxiosResponse<T>> => {
  const prevApiCancelCount: number = store.getters['error/apiCancelCount'];

  try {
    const response: AxiosResponse<T> = await request();

    // キャンセル確認
    const nextApiCancelCount: number = store.getters['error/apiCancelCount'];
    if (prevApiCancelCount !== nextApiCancelCount) throw new ApiCancelError('');

    return response;
  } catch (error) {
    // キャンセル確認
    if (error instanceof ApiCancelError) return Promise.reject(error);
    const nextApiCancelCount: number = store.getters['error/apiCancelCount'];
    if (prevApiCancelCount !== nextApiCancelCount) return Promise.reject(new ApiCancelError(''));

    if (axios.isAxiosError(error) && error.response) {
      // サーバからエラーが返された
      const status: number = error.response.status;
      const code: ErrorCode = (error.response.data && error.response.data.error_code) || '-1';

      // メンテナンス確認
      if (status === 503 && code === ErrorCode.Maintenance) {
        const isFields: boolean = error.response.data.fields && Array.isArray(error.response.data.fields);
        store.commit('maintenance/setMaintenance', true);
        store.commit('maintenance/setStart', { date: isFields ? error.response.data.fields[0] : '' });
        store.commit('maintenance/setEnd', { date: isFields ? error.response.data.fields[1] : '' });
        store.commit('maintenance/setTitle', { title: isFields ? error.response.data.fields[2] : '' });
        store.commit('maintenance/setMessage', { message: isFields ? error.response.data.fields[3] : '' });
        return Promise.reject(new ApiCancelError(''));
      }

      // // 通信失敗ログの送信
      // const level: LogLevel = status >= 500 ? 'ERROR' : 'INFO';
      // const config: AxiosRequestConfig | undefined = error.response.config;
      // const api: string | undefined = config && config.url && config.url.replace(config.baseURL || '', '');
      // const message: string = `${error.message} ${api} ${status} ${code}`;
      // sendLog({ level, kind: 'request_failed', message });

      // エラーをそのまま返す
      return Promise.reject(error);
    } else {
      // 通信失敗ログの送信
      const level: LogLevel = 'INFO';
      const config: AxiosRequestConfig | undefined = axios.isAxiosError(error) ? error.config : undefined;
      const api: string | undefined = config && config.url && config.url.replace(config.baseURL || '', '');
      const message: string = `${error instanceof Error ? error.message : ''} ${api}`;
      sendLog({ level, kind: 'request_timeout', message });

      // エラーをそのまま返す
      return Promise.reject(error);
    }
  }
};

export default {
  get: async <T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    await template<T>(() => client.get<T>(url, config)),

  post: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    await template<T>(() => client.post<T>(url, data, config)),

  put: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    await template<T>(() => client.put<T>(url, data, config)),

  delete: async (url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<void>> =>
    await template<void>(() => client.delete(url, config))
};
